/**
 * RequestInfoPanel — Underwriter/Admin selects fields and custom items to request from borrower/broker.
 * Shows a read-only view for brokers.
 */
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { ClipboardList, Plus, X, Trash2, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FIELD_OPTIONS = {
  sba_loan: [
    { key: 'business_legal_name', label: 'Business Legal Name' }, { key: 'doing_business_as', label: 'DBA' },
    { key: 'legal_entity_type', label: 'Entity Type' }, { key: 'business_start_date', label: 'Business Start Date' },
    { key: 'federal_tax_id', label: 'Tax ID' }, { key: 'business_phone_number', label: 'Business Phone' },
    { key: 'business_address', label: 'Business Address' }, { key: 'naics_code_or_nature', label: 'Industry/NAICS' },
    { key: 'number_of_employees', label: 'Employees' },
    { key: 'first_name', label: 'First Name' }, { key: 'last_name', label: 'Last Name' },
    { key: 'date_of_birth', label: 'Date of Birth' },
    { key: 'social_security_number', label: 'SSN' }, { key: 'mobile_phone_number', label: 'Mobile Phone' },
    { key: 'email_address', label: 'Email' }, { key: 'home_address', label: 'Home Address' },
    { key: 'ownership_percentage', label: 'Ownership %' }, { key: 'loan_requested_amount', label: 'Loan Amount' },
    { key: 'total_monthly_sales', label: 'Monthly Sales' }, { key: 'annual_income', label: 'Annual Income' },
    { key: 'has_existing_debt', label: 'Has Existing Debt' },
    { key: 'assets_cash', label: 'Assets: Cash' }, { key: 'assets_home', label: 'Assets: Home' },
    { key: 'assets_automobile', label: 'Assets: Auto' }, { key: 'assets_investment_accounts', label: 'Assets: Investments' },
    { key: 'liabilities_mortgage', label: 'Liabilities: Mortgage' }, { key: 'liabilities_credit_cards', label: 'Liabilities: Credit Cards' },
    { key: 'liabilities_auto_loans', label: 'Liabilities: Auto Loans' }, { key: 'liabilities_other', label: 'Liabilities: Other' },
  ],
  term_loan_10yr: [
    { key: 'first_name', label: 'First Name' }, { key: 'last_name', label: 'Last Name' },
    { key: 'email_address', label: 'Email' }, { key: 'phone_number', label: 'Phone' },
    { key: 'ownership_percentage', label: 'Ownership %' },
    { key: 'legal_corporate_name', label: 'Corporate Name' }, { key: 'doing_business_as', label: 'DBA' },
    { key: 'business_address', label: 'Business Address' }, { key: 'business_city', label: 'City' },
    { key: 'business_state', label: 'State' }, { key: 'business_zip', label: 'ZIP' },
    { key: 'entity_type', label: 'Entity Type' }, { key: 'federal_tax_id', label: 'Tax ID' },
    { key: 'industry_type', label: 'Industry' }, { key: 'number_of_employees', label: 'Employees' },
    { key: 'annual_revenue', label: 'Annual Revenue' }, { key: 'monthly_revenue', label: 'Monthly Revenue' },
    { key: 'funding_amount_requested', label: 'Funding Requested' }, { key: 'use_of_funds', label: 'Use of Funds' },
  ],
  term_loan_3_5yr: [
    { key: 'business_name', label: 'Business Name' }, { key: 'doing_business_as', label: 'DBA' },
    { key: 'business_structure', label: 'Business Structure' }, { key: 'average_annual_revenue', label: 'Avg Annual Revenue' },
    { key: 'date_established', label: 'Date Established' }, { key: 'federal_tax_id', label: 'Tax ID' },
    { key: 'business_phone', label: 'Business Phone' }, { key: 'naics_code', label: 'NAICS Code' },
    { key: 'business_address', label: 'Address' }, { key: 'business_city', label: 'City' },
    { key: 'business_state', label: 'State' }, { key: 'business_zip', label: 'ZIP' },
    { key: 'first_name', label: 'First Name' }, { key: 'last_name', label: 'Last Name' },
    { key: 'email_address', label: 'Email' }, { key: 'ssn_itin', label: 'SSN/ITIN' },
    { key: 'date_of_birth', label: 'Date of Birth' }, { key: 'ownership_percentage', label: 'Ownership %' },
    { key: 'home_address', label: 'Home Address' }, { key: 'home_city', label: 'Home City' },
    { key: 'home_state', label: 'Home State' }, { key: 'home_zip', label: 'Home ZIP' },
    { key: 'use_of_proceeds', label: 'Use of Proceeds' }, { key: 'total_loan_amount', label: 'Total Loan Amount' },
  ],
  line_of_credit: [
    { key: 'owner_first_name', label: 'Owner First Name' }, { key: 'owner_last_name', label: 'Owner Last Name' },
    { key: 'owner_address', label: 'Owner Address' },
    { key: 'owner_ssn', label: 'SSN' }, { key: 'owner_email', label: 'Email' }, { key: 'owner_phone', label: 'Cell Phone' },
    { key: 'business_name', label: 'Business Name' }, { key: 'business_industry', label: 'Industry' },
    { key: 'years_in_business', label: 'Years in Business' }, { key: 'business_zip_code', label: 'Business Zipcode' },
    { key: 'requested_line_of_credit_amount', label: 'Credit Amount' }, { key: 'purpose_of_credit', label: 'Purpose' },
  ],
  equipment_financing: [
    { key: 'business_name', label: 'Business Name' }, { key: 'business_address', label: 'Address' },
    { key: 'entity_type', label: 'Entity Type' }, { key: 'federal_tax_id', label: 'Tax ID' },
    { key: 'years_in_business', label: 'Years in Business' }, { key: 'industry', label: 'Industry' },
    { key: 'annual_revenue', label: 'Annual Revenue' },
    { key: 'owner_first_name', label: 'Owner First Name' }, { key: 'owner_last_name', label: 'Owner Last Name' },
    { key: 'owner_email', label: 'Email' },
    { key: 'owner_phone', label: 'Phone' }, { key: 'ownership_percentage', label: 'Ownership %' },
    { key: 'amount_requested', label: 'Amount Requested' },
    { key: 'equipment_type', label: 'Equipment Type' }, { key: 'equipment_description', label: 'Equipment Description' },
    { key: 'equipment_cost', label: 'Equipment Cost' },
  ],
};

export const RequestInfoPanel = ({ application, canManage, isBroker }) => {
  const loanType = application.product_type;
  const availableFields = FIELD_OPTIONS[loanType] || [];
  const dataKey = `${loanType}_data`;
  const currentData = application[dataKey] || {};

  const [requestedFields, setRequestedFields] = useState(application?.requested_fields || []);
  const [customInfoRequests, setCustomInfoRequests] = useState(application?.custom_info_requests || []);
  const [newCustomRequest, setNewCustomRequest] = useState('');
  const [savingRequests, setSavingRequests] = useState(false);
  const [showFieldPicker, setShowFieldPicker] = useState(false);

  const emptyFields = availableFields.filter(f => {
    const val = currentData[f.key];
    return val === undefined || val === null || val === '';
  });

  const toggleRequestedField = (fieldKey) => {
    setRequestedFields(prev =>
      prev.includes(fieldKey) ? prev.filter(k => k !== fieldKey) : [...prev, fieldKey]
    );
  };

  const addCustomRequest = () => {
    const text = newCustomRequest.trim();
    if (!text) return;
    setCustomInfoRequests(prev => [...prev, text]);
    setNewCustomRequest('');
  };

  const removeCustomRequest = (index) => {
    setCustomInfoRequests(prev => prev.filter((_, i) => i !== index));
  };

  const saveRequestedInfo = async () => {
    setSavingRequests(true);
    try {
      await axios.put(`${BACKEND_URL}/api/applications/${application.id}/requested-fields`, {
        requested_fields: requestedFields,
        custom_info_requests: customInfoRequests,
      });
      application.requested_fields = requestedFields;
      application.custom_info_requests = customInfoRequests;
      toast.success('Information request sent.');
    } catch (error) {
      console.error('Error saving requested fields:', error);
      toast.error('Failed to save information request.');
    } finally {
      setSavingRequests(false);
    }
  };

  const requestsChanged = JSON.stringify(requestedFields) !== JSON.stringify(application?.requested_fields || [])
    || JSON.stringify(customInfoRequests) !== JSON.stringify(application?.custom_info_requests || []);

  // Broker read-only view
  if (isBroker && (requestedFields.length > 0 || customInfoRequests.length > 0)) {
    return (
      <Card className="lg:col-span-2 border-purple-200 bg-purple-50" data-testid="broker-requested-info">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-purple-600" />
            Information Requested
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-purple-700 mb-3">The underwriter has requested the following information:</p>
          {requestedFields.length > 0 && (
            <div className="space-y-1 mb-3">
              <p className="text-xs font-semibold text-purple-500 uppercase">Application Fields Needed</p>
              <div className="flex flex-wrap gap-1.5">
                {requestedFields.map(fk => {
                  const fieldDef = availableFields.find(f => f.key === fk);
                  return <span key={fk} className="px-2 py-1 text-xs bg-white text-purple-700 border border-purple-200 rounded-full">{fieldDef?.label || fk}</span>;
                })}
              </div>
            </div>
          )}
          {customInfoRequests.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold text-purple-500 uppercase">Additional Requests</p>
              {customInfoRequests.map((req, i) => (
                <div key={i} className="px-3 py-2 bg-white border border-purple-200 rounded-md text-sm text-purple-800">{req}</div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Underwriter/Admin editable view
  if (!canManage) return null;

  return (
    <Card className="lg:col-span-2" data-testid="request-info-section">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="h-5 w-5 text-purple-600" />
          Request Information
          {(requestedFields.length > 0 || customInfoRequests.length > 0) && (
            <Badge className="ml-auto bg-purple-100 text-purple-700 border-purple-200">
              {requestedFields.length + customInfoRequests.length} items
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-slate-500">Select missing application fields and/or add custom information requests for the borrower/broker to fill in.</p>

        {requestedFields.length > 0 && (
          <div className="space-y-1">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Selected Fields</p>
            <div className="flex flex-wrap gap-1.5">
              {requestedFields.map(fk => {
                const fieldDef = availableFields.find(f => f.key === fk);
                return (
                  <button key={fk} onClick={() => toggleRequestedField(fk)}
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-purple-100 text-purple-800 border border-purple-200 rounded-full hover:bg-purple-200 transition-colors"
                    data-testid={`requested-field-${fk}`}>
                    {fieldDef?.label || fk}
                    <X className="h-3 w-3" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <Button variant="outline" size="sm" onClick={() => setShowFieldPicker(!showFieldPicker)} className="text-xs" data-testid="toggle-field-picker-btn">
          {showFieldPicker ? 'Hide' : 'Select'} Application Fields ({emptyFields.length} missing)
        </Button>

        {showFieldPicker && (
          <div className="border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto bg-slate-50">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {emptyFields.map(f => (
                <label key={f.key} className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer hover:bg-white px-2 py-1.5 rounded transition-colors">
                  <input type="checkbox" checked={requestedFields.includes(f.key)} onChange={() => toggleRequestedField(f.key)}
                    className="rounded border-slate-300 text-purple-600 focus:ring-purple-500" />
                  {f.label}
                </label>
              ))}
              {emptyFields.length === 0 && <p className="text-xs text-slate-400 col-span-3">All fields are filled</p>}
            </div>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Custom Requests</p>
          {customInfoRequests.map((req, i) => (
            <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 bg-purple-50 border border-purple-200 rounded-md text-sm" data-testid={`custom-request-${i}`}>
              <span className="text-purple-800">{req}</span>
              <button onClick={() => removeCustomRequest(i)} className="text-purple-400 hover:text-red-500 flex-shrink-0">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          <div className="flex items-center gap-2">
            <Input value={newCustomRequest} onChange={(e) => setNewCustomRequest(e.target.value)}
              placeholder="e.g. Provide a copy of your business license..." className="flex-1 h-8 text-sm"
              onKeyDown={(e) => e.key === 'Enter' && addCustomRequest()} data-testid="custom-request-input" />
            <Button variant="outline" size="sm" onClick={addCustomRequest} disabled={!newCustomRequest.trim()} className="h-8" data-testid="add-custom-request-btn">
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {requestsChanged && (
          <Button size="sm" className="w-full bg-purple-600 hover:bg-purple-700"
            onClick={saveRequestedInfo} disabled={savingRequests} data-testid="save-requested-info-btn">
            <Send className="h-4 w-4 mr-2" />
            {savingRequests ? 'Saving...' : 'Save & Send Request'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default RequestInfoPanel;
